import { useGetProductsWithIndustryQuery } from '@/app/globalRedux/api/filter/companySearchSlice'
import { Product } from '@/app/globalRedux/filter/companyFilter'
import {
  addOrRemoveFvProductFilterSelectedProduct,
  addOrRemovePpProductFilterSelectedProduct,
  addOrRemoveTlProductFilterSelectedProduct,
  setFvProductSelectAllProducts,
  setPpProductSelectAllProducts,
  setTlProductSelectAllProducts,
} from '@/app/globalRedux/filter/productFilter'
import { RootState } from '@/app/globalRedux/store'
import { usePageNumberUpdator } from '@/app/hooks/usePageParamUpdater'
import { useTranslation } from '@/app/i18n/client'
import { ProductWithIndustry } from '@/app/types/constantDataType'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import SearchIcon from '@mui/icons-material/Search'
import { InputAdornment, TextField } from '@mui/material'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

const ProductsFilter = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const goToPage = usePageNumberUpdator()

  const [productText, setProductText] = useState<string>('')
  const productFilter = useSelector((state: RootState) => state.productFilter)
  const { data: productData = [] } = useGetProductsWithIndustryQuery<{
    data: ProductWithIndustry[]
    isLoading: boolean
    isError: boolean
  }>({})

  const fruitProductsList =
    productData.find((product) => product.name === 'AF Production')?.products ||
    []

  const prePostProductList =
    productData.find((product) => product.name === 'AF Pre & Post Harvest')
      ?.products || []

  const transportProductsList =
    productData.find((product) => product.name === 'AF Transports')?.products ||
    []

  const isAllProductsInParticularSector = (sector: string): boolean => {
    switch (sector) {
      case 'Fruit & Vegetable':
        return (
          productFilter?.productFilterFvSelectedProducts?.length ===
          fruitProductsList?.length
        )
      case 'Pre Post Harvest':
        return (
          productFilter?.productFilterPpSelectedProducts?.length ==
          prePostProductList?.length
        )
      case 'Transport / Logistics':
        return (
          productFilter?.productFilterTlSelectedProducts?.length ==
          transportProductsList?.length
        )
      default:
        return false
    }
  }

  return (
    <div className="shadow-[0_0_3px_0_rgba(0,0,0,0.06)] bg-neutral-50 border border-solid border-[#ebebeb] mt-[20px] rounded-[4px] mobile:mt-[10px]">
      <Accordion type="single" collapsible defaultValue="product">
        <AccordionItem value="product">
          <AccordionTrigger className="text-[18px] font-semibold text-[#222]">
            {t('Product')}
          </AccordionTrigger>
          <AccordionContent className="!p-0 ">
            {/* {Product} */}
            <Accordion
              type="single"
              collapsible
              className="!p-0 "
              defaultValue="fruitVegetable"
            >
              <TextField
                className=" !mt-1 w-[100%]"
                value={productText}
                onChange={(event) => {
                  setProductText(event?.target?.value)
                }}
                placeholder={t('Search Products')}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" className="ml-[10px] ">
                      <SearchIcon className="!w-[17.6px] !h-[17.6px]" />
                    </InputAdornment>
                  ),
                }}
              />
              <AccordionItem value="fruitVegetable">
                <AccordionTrigger className="text-[18px] font-semibold text-[#222] px-[0px]">
                  <div className="  flex w-full flex-row  gap-2">
                    <div className="  font-ns_bold  not-italic leading-[normal] tracking-[normal] text-left text-[#222]">
                      {t('Fruit & Vegetable')}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col  gap-[8px]  max-h-[200px] !mt-1 overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    <div className="flex items-center gap-2">
                      {fruitProductsList?.filter((item) =>
                        item?.name
                          ?.toLowerCase()
                          .includes(productText?.toLowerCase())
                      )?.length > 0 ? (
                        <>
                          <Checkbox
                            checked={isAllProductsInParticularSector(
                              'Fruit & Vegetable'
                            )}
                            id="select-all-products"
                            className="border-2 border-input"
                            onClick={() => {
                              dispatch(
                                setFvProductSelectAllProducts(
                                  fruitProductsList ?? []
                                )
                              )
                              goToPage({ newPageNo: 1 })
                            }}
                          />
                          <Label
                            htmlFor="select-all-products"
                            className="text-sm not-italic leading-[normal] tracking-[normal] text-left text-[#6f6f6f]"
                          >
                            {t('Select All Products')}
                          </Label>
                        </>
                      ) : (
                        t('No Products Available')
                      )}
                    </div>
                    {fruitProductsList
                      ?.filter((item) =>
                        item?.name
                          ?.toLowerCase()
                          .includes(productText?.toLowerCase())
                      )
                      ?.map((product: Product) => (
                        <div
                          className=" flex flex-row items-center gap-2 "
                          key={`${product.name}-${product.id}`}
                        >
                          <Checkbox
                            checked={productFilter.productFilterFvSelectedProducts.some(
                              (p: { id: number }) => p.id === product.id
                            )}
                            id={`${product.name}-${product.id}`}
                            className="border-2 border-input"
                            onClick={() => {
                              dispatch(
                                addOrRemoveFvProductFilterSelectedProduct(
                                  product
                                )
                              )
                              goToPage({ newPageNo: 1 })
                            }}
                          />
                          <Label
                            htmlFor={`${product.name}-${product.id}`}
                            className="  text-sm  not-italic leading-[normal] tracking-[normal] text-left text-[#6f6f6f]"
                          >
                            {product.name}
                          </Label>
                        </div>
                      ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <Accordion
              type="single"
              collapsible
              className="!p-0 "
              defaultValue="prepost"
            >
              <AccordionItem value="prepost">
                <AccordionTrigger className="text-[18px] font-semibold text-[#222] px-[0px]">
                  <div className="  flex w-full flex-row  gap-2">
                    <div className="  font-ns_bold  not-italic leading-[normal] tracking-[normal] text-left text-[#222]">
                      {t('Pre Post Harvest')}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col  gap-[8px]  max-h-[200px] !mt-1 overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    <div className="flex items-center gap-2">
                      {prePostProductList?.filter((item) =>
                        item?.name
                          ?.toLowerCase()
                          .includes(productText?.toLowerCase())
                      )?.length > 0 ? (
                        <>
                          <Checkbox
                            checked={isAllProductsInParticularSector(
                              'Pre Post Harvest'
                            )}
                            id="select-all-products"
                            className="border-2 border-input"
                            onClick={() => {
                              dispatch(
                                setPpProductSelectAllProducts(
                                  prePostProductList ?? []
                                )
                              )
                              goToPage({ newPageNo: 1 })
                            }}
                          />
                          <Label
                            htmlFor="select-all-products"
                            className="text-sm not-italic leading-[normal] tracking-[normal] text-left text-[#6f6f6f]"
                          >
                            {t('Select All Products')}
                          </Label>
                        </>
                      ) : (
                        t('No Products Available')
                      )}
                    </div>
                    {prePostProductList
                      ?.filter((item) =>
                        item?.name
                          ?.toLowerCase()
                          .includes(productText?.toLowerCase())
                      )
                      ?.map((product: Product) => (
                        <div
                          className=" flex flex-row items-center gap-2 "
                          key={`${product.name}-${product.id}`}
                        >
                          <Checkbox
                            checked={productFilter.productFilterPpSelectedProducts.some(
                              (p: { id: number }) => p.id === product.id
                            )}
                            id={`${product.name}-${product.id}`}
                            className="border-2 border-input"
                            onClick={() => {
                              goToPage({ newPageNo: 1 })
                              dispatch(
                                addOrRemovePpProductFilterSelectedProduct(
                                  product
                                )
                              )
                            }}
                          />
                          <Label
                            htmlFor={`${product.name}-${product.id}`}
                            className="  text-sm  not-italic leading-[normal] tracking-[normal] text-left text-[#6f6f6f]"
                          >
                            {product.name}
                          </Label>
                        </div>
                      ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <Accordion
              type="single"
              collapsible
              className="!p-0 "
              defaultValue="transportLogistics"
            >
              <AccordionItem value="transportLogistics">
                <AccordionTrigger className="text-[18px] font-semibold text-[#222] px-[0px]">
                  <div className="  flex w-full flex-row  gap-2">
                    <div className="  font-ns_bold  not-italic leading-[normal] tracking-[normal] text-left text-[#222]">
                      {t('Transport / Logistics')}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col  gap-[8px]  max-h-[200px] !mt-1 overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    <div className="flex items-center gap-2">
                      {transportProductsList?.filter((item) =>
                        item?.name
                          ?.toLowerCase()
                          .includes(productText?.toLowerCase())
                      )?.length > 0 ? (
                        <>
                          <Checkbox
                            checked={isAllProductsInParticularSector(
                              'Transport / Logistics'
                            )}
                            id="select-all-products"
                            className="border-2 border-input"
                            onClick={() => {
                              dispatch(
                                setTlProductSelectAllProducts(
                                  transportProductsList ?? []
                                )
                              )
                              goToPage({ newPageNo: 1 })
                            }}
                          />
                          <Label
                            htmlFor="select-all-products"
                            className="text-sm not-italic leading-[normal] tracking-[normal] text-left text-[#6f6f6f]"
                          >
                            {t('Select All Products')}
                          </Label>
                        </>
                      ) : (
                        t('No Products Available')
                      )}
                    </div>
                    {transportProductsList
                      ?.filter((item) =>
                        item?.name
                          ?.toLowerCase()
                          .includes(productText?.toLowerCase())
                      )
                      ?.map((product: Product) => (
                        <div
                          className=" flex flex-row items-center gap-2 "
                          key={`${product.name}-${product.id}`}
                        >
                          <Checkbox
                            checked={productFilter.productFilterTlSelectedProducts.some(
                              (p: { id: number }) => p.id === product.id
                            )}
                            id={`${product.name}-${product.id}`}
                            className="border-2 border-input"
                            onClick={() => {
                              dispatch(
                                addOrRemoveTlProductFilterSelectedProduct(
                                  product
                                )
                              )
                              goToPage({ newPageNo: 1 })
                            }}
                          />
                          <Label
                            htmlFor={`${product.name}-${product.id}`}
                            className="  text-sm  not-italic leading-[normal] tracking-[normal] text-left text-[#6f6f6f]"
                          >
                            {product.name}
                          </Label>
                        </div>
                      ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}

export default ProductsFilter
